/**
 * How a resource is spelled when there may be another one beside it wearing the
 * same name.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A MODULE AND NOT TWO PRIVATE FUNCTIONS
 *
 * Two vendor archives that both ship `police/` are the finding this whole layer
 * exists to produce, which means the duplicate name survives all the way to the
 * output — and every place that *prints* a resource then has to answer the same
 * question: which `police` is this one? There are two such places, they were
 * written a week apart, and they answered it differently.
 *
 * `streamAssets.ts` writes the collision sentence. `report.ts` writes the
 * Markdown heading, the Resources table row and the CSV columns. When those two
 * disagree, the user reads a finding that says "police (from vendor-a.zip) and
 * police (from vendor-b.zip) each ship police.yft", scrolls down to act on it,
 * and finds two `## police — resources` sections that do not distinguish
 * themselves at all. The finding is then correct and unusable, which is the
 * failure this layer is written against, reached by a different road.
 *
 * So the widening rule lives here once and both callers take it.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THE RULE, AND WHY IT IS THIS ONE
 *
 * A qualifier is added only when it disambiguates, and only as far as it has to:
 *
 *   1. The bare name. Right for the single-folder scan that is most of this
 *      tool's traffic, and right for two resources that are already called
 *      different things.
 *   2. The origin, for names that repeat — and only when the origin is not
 *      simply the name again. `intake.fromDirectoryPick` sets the origin to the
 *      first path segment, so picking a folder called `police` yields origin
 *      `police`, and an unconditional qualifier prints `police — police`. A
 *      suffix that is identical on every row, or that restates the row, carries
 *      no information and costs the reader a second of trust.
 *   3. The identity, for the ones the origin could not separate. Origin is
 *      per-container, not per-resource: one `server.zip` holding `[vehicles]/police`
 *      and `[els]/police` — the ordinary shape of a FiveM server folder — gives
 *      both resources the origin `server.zip`. The identity is the drop-relative
 *      root, unique within a drop by construction, so it is the answer that
 *      always works and the one nobody wants to read unless they must.
 *
 * LIMITS
 *
 * Uniqueness is only as good as the identities handed in. They are unique within
 * one drop; a caller that unions two drops can hand two resources the same root,
 * and then step 3 has nothing left to widen with and two labels stay equal. That
 * is not silently papered over here — `collect.ts` reports the duplicate
 * identity to its caller in words — because inventing a `(2)` suffix would tell
 * the reader the two packs differ in a way this layer cannot actually point at.
 *
 * Counting is case-folded through `normalizeName`, because FiveM resolves
 * resource and asset names case-insensitively: `Police` and `police` are one
 * name to the server, so they are one name to the ambiguity test too.
 */

import { normalizeName } from "./normalize";

/** The three things a label can be built from. Origin and identity may be empty. */
export type LabelledResource = {
  name: string;
  identity?: string;
  origin?: string;
};

/**
 * How one caller spells a qualified label.
 *
 * A parameter rather than a shared format, because the two call sites are two
 * different kinds of writing and forcing one on both makes both worse: a finding
 * detail is prose — "police (from vendor-a.zip) and police (from vendor-b.zip)
 * each ship…" — and a Markdown heading is a title, where an em dash reads and a
 * parenthetical does not. What must be shared is *when* to qualify, not how.
 */
export type LabelStyle = {
  /** Stands in for a resource with no name at all. */
  unnamed: string;
  /** Step 2: the name, qualified by where it came from. */
  withOrigin: (name: string, origin: string) => string;
  /** Step 3: whatever step 1 or 2 produced, qualified by the unique root. */
  withIdentity: (label: string, identity: string) => string;
};

/** The style `report.ts` prints in headings, table rows and CSV. */
export const EM_DASH_LABEL_STYLE: LabelStyle = {
  unnamed: "(unnamed resource)",
  withOrigin: (name, origin) => `${name} — ${origin}`,
  withIdentity: (label, identity) => `${label} (${identity})`,
};

/** The style `streamAssets.ts` prints inside a sentence. */
export const PROSE_LABEL_STYLE: LabelStyle = {
  unnamed: "an unnamed resource",
  withOrigin: (name, origin) => `${name} (from ${origin})`,
  withIdentity: (label, identity) => `${label} (${identity})`,
};

function text(value: string | undefined): string {
  return typeof value === "string" ? value : "";
}

/** Which labels in a list occur more than once, counted case-insensitively. */
function repeated(labels: readonly string[]): Set<string> {
  const counts = new Map<string, number>();
  for (const label of labels) {
    const key = normalizeName(label);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const out = new Set<string>();
  for (const [key, count] of counts) if (count > 1) out.add(key);
  return out;
}

/**
 * One label per resource, widened only where two of them would otherwise read
 * alike.
 *
 * Takes the whole list rather than one resource at a time, and that is the whole
 * point: ambiguity is a property of the set, not of the member, so a per-resource
 * function cannot decide it and the two that tried both got it wrong in the same
 * direction — qualifying unconditionally and printing a suffix that distinguishes
 * nothing.
 *
 * Order is preserved, and the result is index-aligned with the input, so a caller
 * can zip it back onto the rows it is rendering.
 */
export function disambiguateLabels(
  resources: readonly LabelledResource[],
  style: LabelStyle,
): string[] {
  const names = resources.map((resource) => {
    const name = text(resource.name);
    return name === "" ? style.unnamed : name;
  });

  // Step 2. Widened against the *name* counts, so a resource whose name is
  // already unique keeps reading as plain prose no matter what it came out of.
  const ambiguousNames = repeated(names);
  const withOrigin = names.map((label, index) => {
    if (!ambiguousNames.has(normalizeName(label))) return label;
    const origin = text(resources[index].origin);
    // An origin that restates the name is the folder-pick case and says nothing.
    if (origin === "" || normalizeName(origin) === normalizeName(label)) return label;
    return style.withOrigin(label, origin);
  });

  // Step 3. Recounted, because step 2 may have separated some pairs and left
  // others — one archive holding two `police/` folders widens to two identical
  // `police — server.zip`, and those are exactly the ones that still need a root.
  const ambiguousLabels = repeated(withOrigin);
  return withOrigin.map((label, index) => {
    if (!ambiguousLabels.has(normalizeName(label))) return label;
    const identity = text(resources[index].identity);
    if (identity === "" || normalizeName(identity) === normalizeName(text(resources[index].name))) {
      return label;
    }
    return style.withIdentity(label, identity);
  });
}
